import {
	createApi,
	fetchBaseQuery,
	type BaseQueryFn,
	type FetchArgs,
	type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

const rawBaseQuery = fetchBaseQuery({
	baseUrl: '/',
	timeout: 3000,
	credentials: 'include',
	prepareHeaders: (headers) => headers,
});

let csrfRequest: Promise<void> | null = null;

const getCookie = (name: string) =>
	document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${name}=`))
		?.split('=')[1];

const ensureCsrfToken = async () => {
	if (getCookie('csrftoken')) return;

	csrfRequest ??= Promise.resolve(rawBaseQuery({ url: '/auth/csrf/' }, {} as any, {}))
		.then(() => undefined)
		.finally(() => {
			csrfRequest = null;
		});
	await csrfRequest;
};

const isUnsafeRequest = (args: string | FetchArgs) => {
	if (typeof args === 'string') return false;
	return !['GET', 'HEAD', 'OPTIONS'].includes((args.method ?? 'GET').toUpperCase());
};

const csrfHeaders = () => {
	const csrfToken = getCookie('csrftoken');
	return csrfToken ? { 'X-CSRFToken': decodeURIComponent(csrfToken) } : {};
};

const isAuthExchangeRequest = (args: string | FetchArgs) => {
	const url = typeof args === 'string' ? args : args.url;
	return url === '/auth/yandex/' || url === '/auth/yandex-dev/';
};

export interface AuthUser {
	id: number;
	email: string;
	username: string;
	first_name: string;
	last_name: string;
	avatar_url: string | null;
}

export interface AuthResponse {
	success: boolean;
	is_mock: boolean;
	user: AuthUser;
}

const baseQueryWithRefresh: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	if (isUnsafeRequest(args)) {
		await ensureCsrfToken();
		const csrfToken = getCookie('csrftoken');
		if (csrfToken && typeof args !== 'string') {
			args = {
				...args,
				headers: {
					...args.headers,
					'X-CSRFToken': decodeURIComponent(csrfToken),
				},
			};
		}
	}

	let result = await rawBaseQuery(args, api, extraOptions);

	if (result.error?.status === 401 && !isAuthExchangeRequest(args)) {
		await ensureCsrfToken();
		const refreshResult = await rawBaseQuery(
			{ url: '/auth/refresh/', method: 'POST', headers: csrfHeaders() },
			api,
			extraOptions,
		);

		// RefreshAPIView returns 204, so successful refresh has no `data`.
		if (!refreshResult.error) {
			result = await rawBaseQuery(args, api, extraOptions);
		}
	}

	return result;
};

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithRefresh,
	// keepUnusedDataFor: 0, // Отключает кеширование (для теста)
	tagTypes: ['PVT', "water-content", "hydrates", "system_plot", "VLP"],
	endpoints: (builder) => ({
		yandexAuth: builder.mutation<AuthResponse, { code: string }>({
			query: (body) => ({ url: '/auth/yandex/', method: 'POST', timeout: 15000, body }),
		}),
		yandexAuthDev: builder.mutation<AuthResponse, { code: string }>({
			query: (body) => ({ url: '/auth/yandex-dev/', method: 'POST', timeout: 15000, body }),
		}),
		logout: builder.mutation<void, void>({
			query: () => ({ url: '/auth/logout/', method: 'POST' }),
		}),
		getCalculator: builder.query({
			query: (params: Record<string, string>) => {
				const searchParams = new URLSearchParams();
				let calculatorType = "";
				Object.entries(params).forEach(([key, value]) => {
					if (key == "calculator_type") {
						calculatorType = value
					} else
						if (value !== null && value !== undefined) {
							searchParams.append(key, String(value));
						}
				});
				return `/api/calculator/${calculatorType}?${searchParams.toString()}`;
			},
			// providesTags: (result, error, id) => [{ type: 'Calculator', id: 'LIST' }],
		}),
		calculator: builder.mutation({
			query: (params: Record<string, string>) => {
				const searchParams = new URLSearchParams();
				let calculatorType = "";
				Object.entries(params).forEach(([key, value]) => {
					if (key == "calculator_type") {
						calculatorType = value
					} else
						if (value !== null && value !== undefined) {
							searchParams.append(key, String(value));
						}
				});
				return `/api/calculator/${calculatorType}?${searchParams.toString()}`;
			},
			// providesTags: (result, error, id) => [{ type: 'Calculator', id: 'LIST' }],
		}),
		getFile: builder.mutation<Blob, Record<string, string>>({
			query: (params) => {
				const searchParams = new URLSearchParams();
				let calculatorType = "";

				Object.entries(params).forEach(([key, value]) => {
					if (key === "calculator_type") {
						calculatorType = value;
					} else if (value !== null && value !== undefined) {
						searchParams.append(key, String(value));
					}
				});

				return {
					url: `/api/calculator/${calculatorType}?${searchParams.toString()}&excel=1`,
					method: 'GET',
					responseHandler: async (response) => {
						if (!response.ok) {
							return response.json().catch(() => response.text());
						}
						return response.blob();
					},
				};
			},
		}),
	}),
});


export const {
	useYandexAuthMutation,
	useYandexAuthDevMutation,
	useLogoutMutation,
	useGetCalculatorQuery,
	useLazyGetCalculatorQuery,
	useGetFileMutation,
	useCalculatorMutation,
} = apiSlice;
